import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import appLogo from '../assets/logo.svg';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Zap, Mail, Lock, User, Building2, ArrowRight, Check, Eye, EyeOff, Apple } from 'lucide-react';
import { LanguageSwitcher } from '../components/LanguageSwitcher';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    organizationName: '',
    createOrg: false
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await register(
        formData.name,
        formData.email,
        formData.password,
        formData.createOrg ? formData.organizationName : null
      );
      toast.success('Account created! Please verify your email.');
      navigate('/verify-email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    t('landing.featureUnlimited'),
    t('projects.title'),
    t('landing.featureTeamMgmt'),
    t('landing.featureDetailedReports'),
    t('landing.featurePrioritySupport')
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left side - Brand */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-slate-900">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-600/20 via-slate-900 to-cyan-600/20" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAzMHYySDI0di0yaDEyek0zNiAyNnYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
        
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          {/* Logo */}
          <div className="flex items-center gap-2.5">
            <img src={appLogo} alt="Inspiro Tracker" className="w-12 h-12 rounded-xl object-contain" />
            <span className="text-2xl font-bold tracking-tight">Inspiro Tracker</span>
          </div>
          
          {/* Main content */}
          <div className="max-w-md">
            <h1 className="text-4xl font-semibold mb-4 leading-tight tracking-tight">
              {t('landing.heroTitle')}<br />
              {t('landing.heroTitleHighlight')}<br />
              <span className="text-gradient bg-gradient-to-r from-sky-400 to-cyan-400 bg-clip-text text-transparent">{t('common.appName')}</span>
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed mb-8">
              {t('landing.heroSubtitle')}
            </p>
            
            {/* Features list */}
            <div className="space-y-3">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-sky-500/20 flex items-center justify-center">
                    <Check className="w-3 h-3 text-sky-400" />
                  </div>
                  <span className="text-sm text-slate-300">{feature}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Footer */}
          <p className="text-sm text-slate-500">
            Trusted by 10,000+ teams worldwide
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-slate-50 dark:bg-slate-950">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2.5 mb-8 justify-center">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-500 to-cyan-500 flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight">
              {t('common.appName')}
            </span>
          </div>

          <div className="flex justify-end mb-4">
            {/* Language Switcher - Disabled */}
          </div>

          <Card className="border-0 shadow-xl shadow-slate-200/50 dark:shadow-none bg-white dark:bg-slate-900">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-xl font-semibold tracking-tight">
                {t('auth.registerTitle')}
              </CardTitle>
              <CardDescription className="text-slate-500 text-sm">
                {t('auth.registerSubtitle')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="name" className="enterprise-label">{t('common.name')}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder={t('auth.namePlaceholder')}
                      value={formData.name}
                      onChange={handleChange}
                      className="pl-10 h-10 enterprise-input"
                      required
                      data-testid="register-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="email" className="enterprise-label">{t('common.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder={t('auth.emailPlaceholder')}
                      value={formData.email}
                      onChange={handleChange}
                      className="pl-10 h-10 enterprise-input"
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="password" className="enterprise-label">{t('common.password')}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••"
                        value={formData.password}
                        onChange={handleChange}
                        className="pl-10 pr-10 h-10 enterprise-input"
                        required
                        data-testid="register-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                        data-testid="register-toggle-password"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="confirmPassword" className="enterprise-label">{t('settings.confirmPassword')}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="••••••"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className="pl-10 pr-10 h-10 enterprise-input"
                        required
                        data-testid="register-confirm-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                        data-testid="register-toggle-confirm-password"
                        tabIndex={-1}
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="pt-1 space-y-2">
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="createOrg"
                      checked={formData.createOrg}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, createOrg: checked }))}
                      className="mt-0.5"
                      data-testid="create-org-checkbox"
                    />
                    <div>
                      <Label htmlFor="createOrg" className="text-sm text-slate-700 dark:text-slate-300 font-medium cursor-pointer">
                        I'm setting up for a team / organization
                      </Label>
                      <p className="text-xs text-slate-400 mt-0.5">
                        Select this to create your company workspace with team management features.
                      </p>
                    </div>
                  </div>
                  {!formData.createOrg && (
                    <p className="text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2 rounded-lg" data-testid="individual-plan-info">
                      You'll be signed up on the <span className="font-semibold">Individual plan — free forever</span>. No organization details needed. You can always upgrade later.
                    </p>
                  )}
                </div>

                {formData.createOrg && (
                  <div className="space-y-1.5 animate-fade-in">
                    <Label htmlFor="organizationName" className="enterprise-label">{t('auth.orgPlaceholder')}</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="organizationName"
                        name="organizationName"
                        type="text"
                        placeholder="Acme Inc."
                        value={formData.organizationName}
                        onChange={handleChange}
                        className="pl-10 h-10 enterprise-input"
                        required={formData.createOrg}
                        data-testid="register-org-name-input"
                      />
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-10 bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 text-white font-medium rounded-lg transition-all duration-200 shadow-sm shadow-sky-500/20 mt-2"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {t('common.loading')}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      {t('landing.getStarted')} <ArrowRight className="w-4 h-4" />
                    </span>
                  )}
                </Button>
              </form>

              {/* SSO Divider */}
              <div className="relative mt-5 mb-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-slate-200 dark:border-slate-700" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white dark:bg-slate-900 px-2 text-slate-400">or sign up with</span>
                </div>
              </div>

              {/* SSO Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/sso/google`;
                  }}
                  className="h-10 rounded-lg border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 font-medium text-sm"
                  data-testid="register-google-sso-btn"
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/sso/microsoft`;
                  }}
                  className="h-10 rounded-lg border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 font-medium text-sm"
                  data-testid="register-microsoft-sso-btn"
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 23 23">
                    <path fill="#f35325" d="M1 1h10v10H1z"/>
                    <path fill="#81bc06" d="M12 1h10v10H12z"/>
                    <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                    <path fill="#ffba08" d="M12 12h10v10H12z"/>
                  </svg>
                  Microsoft
                </Button>
              </div>

              <div className="mt-5 text-center">
                <p className="text-slate-500 text-sm">
                  {t('auth.hasAccount')}{' '}
                  <Link 
                    to="/login" 
                    className="text-sky-600 hover:text-sky-700 font-medium transition-colors"
                    data-testid="goto-login-link"
                  >
                    {t('auth.login')}
                  </Link>
                </p>
              </div>

              <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                <p className="text-xs text-slate-400 text-center mb-2">Download Desktop App</p>
                <div className="flex items-center justify-center gap-3">
                  <a href="https://api.inspiroworks.com/downloads/InspiroTracker.exe" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors" data-testid="register-download-windows">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801"/></svg>
                    Windows
                  </a>
                  <span className="text-slate-300 dark:text-slate-700">|</span>
                  <a href="https://api.inspiroworks.com/downloads/InspiroTracker.dmg" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition-colors" data-testid="register-download-mac">
                    <Apple className="w-3.5 h-3.5" />
                    macOS
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Register;
